/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import { SYS_SIZE_FIXED_1200 } from '@oneaudi/unified-web-alpha-common-v2';
import { useI18n } from '@oneaudi/i18n-context';
import { createPreloadAction, createSetActiveItemAction } from '../../actions/header-actions';
import type { AudiHeaderSearchButtonProps } from '../../interfaces/header-components.interfaces';
import type { HeaderStyledSearchButtonProps } from '../../interfaces/header-styled-components.interfaces';
import { ServiceContext } from '../../context/ServiceContext';
import { ConfigContext } from '../../context/ConfigContext';
import { NAVIGATION_CLASSNAME, NAVIGATION_MOBILE_CLASSNAME } from '../../utils/globals';
import { useSearchTracking } from '../../hooks/use-search-tracking';
import { IconButton } from '@oneaudi/unified-web-components';
import AiSearchIcon from './AiSearchIcon';

const HeaderStyledSearchButton = styled.div<HeaderStyledSearchButtonProps>`
  align-items: center;
  display: none;
  margin: var(--one-header-space-s) 0;

  @media screen and (min-width: ${({ theme: { breakpoints } }) => breakpoints.l}px) {
    display: flex;
    align-items: center;
    margin-right: var(--one-header-space-s);
  }

  margin-right: 0 !important;

  & button {
    background-color: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    padding: 0;
  }

  & button:focus {
    outline: none;
  }

  & button:focus-visible {
    outline: auto;
  }

  & button svg {
    height: 24px;
    width: 24px;
  }
`;

export const AiSearchButton = styled.button`
  align-items: center;
  background-color: transparent;
  border: none;
  border-radius: 50%;
  color: var(--one-header-color-white);
  cursor: pointer;
  display: flex;
  height: ${SYS_SIZE_FIXED_1200};
  justify-content: center;
  padding: 0;
  transition:
    color,
    box-shadow,
    border-color,
    background 250ms cubic-bezier(0.75, 0.02, 0.5, 1);
  width: ${SYS_SIZE_FIXED_1200};

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: auto;
  }

  &:hover {
    background-color: var(--one-header-icon-button-hover-fill);
    box-shadow: inset 0 0 0 1px var(--one-header-icon-button-hover-stroke);
    color: var(--one-header-icon-button-hover-color);
  }

  & svg {
    color: inherit;
    height: 24px;
    width: 24px;
  }
`;

const AudiHeaderSearchButtonAlpha: React.FC<AudiHeaderSearchButtonProps> = (props) => {
  const { headerStateService, trackingService } = useContext(ServiceContext);
  const { useAiSearch } = useContext(ConfigContext);
  const { headerDispatch, headerNavigationItemsAmount, isOpen } = props;

  let isSmallView = true;
  headerStateService.registerCallback((headerStateRemote) => {
    isSmallView = headerStateRemote.isMobileView;
  });

  const SearchAriaLabel = useI18n({
    defaultMessage: 'Search',
    id: 'nemo.ui.common.search-button',
  });

  const searchAccessEvent = useSearchTracking({
    useAiSearch,
    searchLabel: SearchAriaLabel,
    navigationItemsAmount: headerNavigationItemsAmount,
  });

  useEffect(() => {
    const searchButton = document.querySelector('#search-button');
    searchButton?.classList.add(NAVIGATION_CLASSNAME, NAVIGATION_MOBILE_CLASSNAME);
  }, []);

  const handleSearchAccess: (event: React.MouseEvent<HTMLButtonElement>) => void = (event) => {
    event.preventDefault();
    // event tracking
    trackingService?.track(searchAccessEvent);
    // dispatch status update
    headerDispatch(
      createSetActiveItemAction({
        anchor: null,
        headerStateService,
        index: -1,
        showSearch: true,
        showDealer: false,
        showDealerSearch: false,
        showLoginFlyout: false,
        showWistListFlyout: false,
        openDealerDetails: false,
      }),
    );
  };

  const handleFocusEvent: (
    event:
      | React.FocusEvent<HTMLButtonElement>
      | React.MouseEvent<HTMLButtonElement>
      | React.TouchEvent<HTMLButtonElement>,
  ) => void = () => {
    // @ts-ignore
    headerDispatch(createPreloadAction());
  };

  return (
    <HeaderStyledSearchButton
      headerNavigationItemsAmount={headerNavigationItemsAmount}
      isSmallView={isSmallView}
      isOpen={isOpen}
    >
      {useAiSearch ? (
        <AiSearchButton
          aria-label={SearchAriaLabel}
          id="search-button"
          onClick={handleSearchAccess}
          onMouseEnter={handleFocusEvent}
          onTouchStart={handleFocusEvent}
          title={SearchAriaLabel}
          type="button"
        >
          <AiSearchIcon />
        </AiSearchButton>
      ) : (
        <IconButton
          aria-label={SearchAriaLabel}
          icon="search"
          id="search-button"
          onClick={handleSearchAccess}
          // @ts-expect-error
          onMouseEnter={handleFocusEvent}
          onTouchStart={handleFocusEvent}
          size="sm"
          title={SearchAriaLabel}
          variant="tertiary"
          type="button"
        />
      )}
    </HeaderStyledSearchButton>
  );
};

export default AudiHeaderSearchButtonAlpha;
